import axios from "axios";

const normalizeApiBaseUrl = (url?: string) => {
  return url?.replace(/\/api\/?$/, "");
};

const baseURL =
  process.env.NODE_ENV === "development"
    ? `http://${window.location.hostname}:8080`
    : normalizeApiBaseUrl(process.env.REACT_APP_BASE_URL);

export const integratorApi = axios.create({
  baseURL,
});

integratorApi.interceptors.request.use((config) => {
  if (typeof config.url === "string") {
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(config.url) || config.url.startsWith("//");

    if (!isAbsoluteUrl) {
      const normalizedUrl = config.url.startsWith("/") ? config.url : `/${config.url}`;
      config.url = normalizedUrl === "/api" || normalizedUrl.startsWith("/api/")
        ? normalizedUrl
        : `/api${normalizedUrl}`;
    }
  }

  return config;
});

export const integratorLogin = async (email: string, password: string) => {
  return integratorApi.post("/integrator-auth/login", { email, password });
};

export const validateIntegratorResetToken = async (token: string) => {
  return integratorApi.get("/integrator-auth/validate-reset-token", { params: { token } });
};

export const resetIntegratorPassword = async (token: string, password: string) => {
  return integratorApi.post("/integrator-auth/reset-password", { token, password });
};

export const getIntegratorMe = async () => {
  return integratorApi.get("/integrator-auth/me");
};

export const getIntegratorDashboard = async (clientId: number) => {
  return integratorApi.get("/integrator/dashboard", {
    params: { clientId },
  });
};

export const listIntegratorProjects = async (clientId: number) => {
  return integratorApi.get("/integrator/projects", {
    params: { clientId },
  });
};

export const getIntegratorProject = async (projectId: number, clientId: number) => {
  return integratorApi.get(`/integrator/projects/${projectId}`, {
    params: { clientId },
  });
};

export const getIntegratorProjectDocumentLink = async (projectId: number, documentId: number, clientId: number) => {
  return integratorApi.get(`/integrator/projects/${projectId}/documents/${documentId}`, {
    params: { clientId },
  });
};

export const listIntegratorProjectRequests = async (clientId: number) => {
  return integratorApi.get("/integrator/project-requests", {
    params: { clientId },
  });
};

export const createIntegratorProjectRequest = async (payload: unknown) => {
  return integratorApi.post("/integrator/project-requests", payload);
};

export const getIntegratorProjectRequestOptions = async (clientId: number) => {
  return integratorApi.get("/integrator/project-request-options", { params: { clientId } });
};

export const issueIntegratorInspectionCharge = async (projectId: number, clientId: number) => {
  return integratorApi.post(`/integrator/projects/${projectId}/inspection-charge`, null, {
    params: { clientId },
  });
};

export const getIntegratorProjectPaymentFile = async (projectId: number, paymentId: number, clientId: number) => {
  return integratorApi.get(`/integrator/projects/${projectId}/payments/${paymentId}/file`, {
    params: { clientId },
    responseType: "blob",
  });
};

export const createIntegratorInspectionRequest = async (
  projectId: number,
  clientId: number,
  notes: string,
  files: File[] = []
) => {
  const { data } = await createIntegratorProjectRequest({
    clientId,
    requestKind: "INSPECTION",
    relatedProjectId: projectId,
    notes,
  });
  if (files.length) {
    await uploadIntegratorProjectRequestAttachments(
      data.request.id,
      clientId,
      files,
      files.map(() => "OTHER")
    );
  }
  return data;
};

export const getIntegratorBillingSummary = async (clientId: number) => {
  return integratorApi.get("/integrator/billing-summary", { params: { clientId } });
};

export const getIntegratorProjectDocumentFile = async (projectId: number, documentId: number, clientId: number) => {
  return integratorApi.get(`/integrator/projects/${projectId}/documents/${documentId}/file`, { params: { clientId }, responseType: "blob" });
};

export const discardIntegratorProjectRequest = async (requestId: number, clientId: number) => {
  return integratorApi.delete(`/integrator/project-requests/${requestId}/discard`, { params: { clientId } });
};

export const uploadIntegratorProjectRequestAttachments = async (
  requestId: number,
  clientId: number,
  files: File[],
  categories: string[] = [],
  replacementAttachmentIds: Array<number | null> = []
) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  formData.append("categories", JSON.stringify(categories));
  if (replacementAttachmentIds.length) {
    formData.append("replacementAttachmentIds", JSON.stringify(replacementAttachmentIds));
  }
  return integratorApi.post(`/integrator/project-requests/${requestId}/attachments`, formData, {
    params: { clientId },
  });
};

export const getIntegratorProjectRequest = async (requestId: number, clientId: number) => {
  return integratorApi.get(`/integrator/project-requests/${requestId}`, { params: { clientId } });
};

export const getIntegratorProjectRequestAttachmentFile = async (requestId: number, attachmentId: number, clientId: number) => {
  return integratorApi.get(`/integrator/project-requests/${requestId}/attachments/${attachmentId}/file`, { params: { clientId }, responseType: "blob" });
};

export const cancelIntegratorProjectRequest = async (requestId: number, clientId: number) => {
  return integratorApi.patch(`/integrator/project-requests/${requestId}/cancel`, null, { params: { clientId } });
};

export const listIntegratorNotifications = async (clientId: number) => {
  return integratorApi.get("/integrator/notifications", {
    params: { clientId },
  });
};

export const getIntegratorUnreadNotificationsCount = async (clientId: number) => {
  return integratorApi.get("/integrator/notifications/unread-count", {
    params: { clientId },
  });
};

export const getIntegratorPushPublicKey = async () => {
  return integratorApi.get("/integrator/notifications/push-public-key");
};

export const saveIntegratorPushSubscription = async (subscription: PushSubscriptionJSON) => {
  return integratorApi.post("/integrator/notifications/push-subscriptions", { subscription });
};

export const deleteIntegratorPushSubscription = async (endpoint: string) => {
  return integratorApi.delete("/integrator/notifications/push-subscriptions", { data: { endpoint } });
};

export const markIntegratorNotificationAsRead = async (notificationId: number) => {
  return integratorApi.patch(`/integrator/notifications/${notificationId}/read`);
};
